import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { sdk } from '@/graphql/client'
import type { LoggedUserFragment } from '@/graphql/generated/sdk'
import { resetSessionRestoreFlag } from '@/router/guards'

type LoggedUser = LoggedUserFragment
type InstanceRights = NonNullable<LoggedUser['rights']>

export const useAuthStore = defineStore('auth', () => {
    const user = ref<LoggedUser | null>(null)
    const switching = ref(false)
    const invalidationKey = ref(0)

    const isAuthenticated = computed(() => user.value !== null)
    const activeInstance = computed(() => user.value?.activeInstance ?? null)
    const rights = computed(() => user.value?.rights ?? null)
    const instances = computed(() =>
        (user.value?.instances ?? []).map(r => r?.instance).filter((i): i is NonNullable<typeof i> => i != null)
    )

    async function login(input: { username: string; password: string }) {
        const r = await sdk.AdminLogin(input)
        const data = r.admin?.user?.login
        if (data?.ok) {
            user.value = data.user ?? null
        }
        return { ok: Boolean(data?.ok), message: null }
    }

    async function logout() {
        try {
            await sdk.AdminLogout()
        } catch {
            // ignore — clear local state regardless
        }
        user.value = null
        invalidationKey.value++
        resetSessionRestoreFlag()
    }

    async function fetchCurrentUser() {
        const r = await sdk.AdminCurrentUser()
        user.value = r.admin?.user?.logged ?? null
        return user.value
    }

    async function changeInstance(instanceName: string) {
        switching.value = true
        try {
            const r = await sdk.AdminChangeInstance({ instanceName })
            if (r.admin?.user?.changeInstance?.ok) {
                await fetchCurrentUser()
                invalidationKey.value++
                return true
            }
            return false
        } finally {
            switching.value = false
        }
    }

    function hasRight(name: keyof InstanceRights): boolean {
        if (user.value?.isSuperuser) return true
        const r = rights.value
        return Boolean(r && r[name])
    }

    return {
        user,
        switching,
        invalidationKey,
        isAuthenticated,
        activeInstance,
        rights,
        instances,
        login,
        logout,
        fetchCurrentUser,
        changeInstance,
        hasRight
    }
})
